import React from 'react'
import { ConfigProvider, TreeSelect } from 'antd'
import cls from 'classnames'
import styled from 'styled-components'

import { debounce } from 'utils/helpers'
import {
  buildTreeArray,
  constructSelectorLabels,
  getSelectedGroups,
  participantsObjectToArray,
  tagRenderer,
} from 'utils/participantsSelector'

import { RECIPIENTS_KEY } from 'containers/Settings/Notifications/EndUser/Editor/constants'

import Chip from 'components/lib/DesignSystem/Chip'
import { ChevronDown } from 'components/lib/DesignSystem/Icons'
import Tooltip from 'components/lib/DesignSystem/Tooltip'

const SELECTED_THRESHOLD = 2

class RecipientsSelector extends React.Component {
  state = {
    selectedRecipientsArray: undefined,
    treeKey: 0,
  }

  componentDidMount() {
    this.onSearchDb = debounce(this.props.onSearchHandler, 100)
    this.onSelectDb = debounce(this.props.onChangeHandler, 100)

    if (this.props.alreadySelectedRecipients) {
      // init app with already selected data
      this.setState({
        selectedRecipientsArray: participantsObjectToArray(this.props.alreadySelectedRecipients),
      })
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const newState = {}

    if (this.props.alreadySelectedRecipients !== nextProps.alreadySelectedRecipients) {
      newState.selectedRecipientsArray = participantsObjectToArray(
        nextProps.alreadySelectedRecipients
      )
    }

    if (Object.keys(newState).length) {
      this.setState(newState)
    }
  }

  selectChangeHandler = (recipientsData, selectorType) => {
    switch (selectorType) {
      case RECIPIENTS_KEY.SELECTED:
        this.setState({
          selectedRecipientsArray: recipientsData,
        })
        this.onSelectDb(getSelectedGroups(recipientsData), selectorType, {
          [RECIPIENTS_KEY.SELECTED]: this.props.alreadySelectedRecipients,
        })
        break
      default:
        break
    }
  }

  handleSearch = (search) => {
    this.onSearchDb(search)
  }

  handleTreeLeave = () => {
    if (this.props.searchTerm?.length > 0) {
      this.props.onSearchHandler('')
    }
    // this will make sure the tree labels are re-rendered after treeLeave
    // which will make sure everything is synced and it will also close the opened sections
    this.setState({ treeKey: Date.now() })
  }

  render() {
    const { selectedRecipientsArray, treeKey } = this.state
    const { className, data, id } = this.props

    return (
      <div
        id={`${id}-container`}
        className={cls(className, 'is-antd-advancedSelector', 'notranslate', 'is-antd')}
      >
        <div className="recipients__title">Recipients</div>
        <ConfigProvider
          theme={{
            token: {
              fontFamily: 'Figtree',
            },
          }}
          getPopupContainer={(triggerNode) => triggerNode.parentElement}
        >
          <div className="campaignStep-campaignSetup-participantsTree-include">
            <TreeSelect
              key={treeKey}
              treeIcon
              showArrow
              id={`${id}-include`}
              showSearch
              className="selector-custom-style"
              value={selectedRecipientsArray}
              dropdownStyle={{
                maxHeight: 800,
                overflow: 'auto',
              }}
              filterTreeNode={false}
              placeholder="Search Names, Departments or Tags"
              allowClear
              multiple
              treeData={buildTreeArray(data || {}, true, constructSelectorLabels)}
              onChange={(val) => {
                this.selectChangeHandler(val, RECIPIENTS_KEY.SELECTED)
              }}
              onBlur={this.handleTreeLeave}
              onSearch={this.handleSearch}
              tagRender={tagRenderer}
              maxTagCount={SELECTED_THRESHOLD}
              maxTagPlaceholder={
                <Tooltip
                  placement="bottom"
                  getPopupContainer={() => document.body}
                  overlayInnerStyle={{
                    padding: '16px',
                    paddingBottom: '6px',
                    width: 'fit-content',
                  }}
                  text={
                    <div>
                      {selectedRecipientsArray?.slice(SELECTED_THRESHOLD)?.map((value) => (
                        <div style={{ marginBottom: '10px' }}>{tagRenderer({ value })}</div>
                      ))}
                    </div>
                  }
                >
                  <Chip
                    noMaxWidth
                    checked
                    checkedStyle="selected"
                    label={`+ ${selectedRecipientsArray?.length - SELECTED_THRESHOLD}...`}
                  />
                </Tooltip>
              }
              suffixIcon={<ChevronDown className="check-icon" fill="#565B6D" />}
            />
          </div>
        </ConfigProvider>
      </div>
    )
  }
}

export default styled(RecipientsSelector)`
  margin-right: 15px;
  .recipients {
    &__title {
      color: #777d8e;
      font-size: 13px;
      margin: 0 0 4px;
    }
  }

  .ant-select-clear {
    width: 20px;
    height: 20px;
    margin-top: -10px;
    right: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ant-select-selector {
    min-height: 36px;
  }

  .ant-select-focused:not(.ant-select-disabled).ant-select:not(.ant-select-customize-input) {
    .ant-select-selector {
      border-color: #2e41ea;
      box-shadow: none;
    }
  }

  .ant-select:not(.ant-select-disabled):hover,
  .ant-select:not(.ant-select-customize-input) {
    .ant-select-selector {
      border: ${({ theme }) => `1px solid ${theme.ds_gray_6}`} !important;
      border-radius: 4px;
    }
  }

  .ant-select-arrow {
    width: 20px;
    height: 20px;
    margin-top: -10px;
    top: 50%;
  }

  .ant-select-selection-placeholder {
    color: #8e8fa0;
  }

  .divider {
    max-width: 400px;
    max-width: 100%;
    margin: 1em 0;
    border-bottom: 1px solid #d1d1d1;
    display: inline-block;
  }

  .ant-tree-select-dropdown {
    padding: 0;
  }

  .ant-select-tree-switcher-noop {
    display: none;
  }

  .ant-select-tree-treenode {
    padding-bottom: 0px !important;
    padding-left: 0px !important;
    width: 100%;
  }

  .ant-select-tree-switcher {
    position: absolute !important;
    width: 100% !important;
    left: 0;
    z-index: 1;

    span {
      opacity: 0;
    }
  }

  .ant-select-tree-indent {
    display: none;
  }

  .participants-icon {
    width: 15px !important;
    height: 15px !important;
    margin-right: 7px;
  }

  .participants-loader-container {
    display: flex;

    .participants-loader-text {
      margin-left: 2em;
    }
  }

  .selector-label-metadata {
    position: absolute;
    right: 4px;
  }

  .selector-label-text {
    width: 75%;
    display: inline-flex;
  }

  .ant-select-tree-node-content-wrapper {
    margin: 0px !important;
    padding: 0px !important;
  }

  .ant-select-tree .ant-select-tree-node-content-wrapper.ant-select-tree-node-selected {
    background-color: #bae7ff;
    border-radius: 0;
  }

  .ant-tag-close-icon {
    pointer-events: auto !important;
  }

  .ant-select-tree-list > div {
    margin-top: 0px !important;
  }

  .label-container {
    padding-left: 8px;
  }

  .main-category-container {
    margin-bottom: 2px;
    padding-left: 8px;
    background: #eef4f7;
  }

  .selector-custom-style {
    width: 400px;
    max-width: 100%;

    .ant-select-selection-placeholder {
      color: #aaabb8;
    }
  }

  .custom-tag,
  .ant-select-selection-item {
    color: #fff;
    font-weight: 500;
    margin-right: 3px;
    border-radius: 5px;
    text-align: center;
    background-color: ${({ theme }) => theme.ds_gray_1};
    font-size: 12px;
    height: 23.2px;
  }

  .ant-select-dropdown {
    z-index: ${(props) => props.zIndexDropdown}!important;
  }

  .ant-select-multiple.ant-select-disabled.ant-select:not(.ant-select-customize-input) {
    .ant-select-selector {
      cursor: default;
      background-color: #fff;
      border: ${({ theme }) => `1px solid ${theme.ds_gray_7}`} !important;
    }

    .custom-tag {
      background-color: ${({ theme }) => theme.ds_gray_5} !important;
    }

    svg,
    svg > path {
      fill: #d9d9d9;
    }
  }
  .ant-select-multiple .ant-select-selection-item {
    height: 26px;
    background-color: white;
    padding-inline-start: 0;
  }
  .ant-select-multiple .ant-select-selection-overflow {
    gap: 5px;
  }
`
